const AUTH_ENTRY_SEEN_KEY = 'wb-parts.auth-entry-seen';

export function renderAuthExperience(app, { authClient, onSignedIn }) {
  const state = {
    view: getInitialAuthView(),
    email: '',
    username: '',
    session: '',
    pendingPassword: '',
    notice: '',
    noticeType: ''
  };

  const show = (view, options = {}) => {
    state.view = view;
    state.notice = options.notice || '';
    state.noticeType = options.noticeType || '';
    render();
  };

  const render = () => {
    app.innerHTML = `
      <section class="auth-page" aria-labelledby="auth-title">
        <div class="auth-card">
          <header class="auth-brand">
            <img src="/assets/branding/full-wbservice-logo.png" alt="W&amp;B Service Company">
            <h1 id="auth-title">W&amp;B Parts Escalation</h1>
          </header>

          ${renderNotice(state.notice, state.noticeType)}
          ${renderView(state, {
            hasMicrosoftSignIn: authClient.hasMicrosoftSignIn(),
            lastSignInMethod: authClient.getLastSignInMethod()
          })}
        </div>
        <p class="auth-help">
          Need help?
          <a href="mailto:support@wbservice.com?subject=Support%20Request%20%E2%80%93%20W%26B%20Parts%20Escalation%20Portal">Contact us</a>
        </p>
      </section>
    `;
    bindView();
  };

  const bindView = () => {
    for (const element of app.querySelectorAll('[data-auth-view]')) {
      element.addEventListener('click', () => {
        const emailInput = app.querySelector('input[type="email"]');
        if (emailInput) {
          state.email = normalizeEmail(emailInput.value);
        }
        show(element.dataset.authView);
      });
    }

    const microsoftButton = app.querySelector('#microsoft-sign-in');
    if (microsoftButton) {
      microsoftButton.addEventListener('click', async () => {
        await runBusy(microsoftButton, async () => authClient.signInWithMicrosoft());
      });
    }

    for (const button of app.querySelectorAll('[data-auth-password-toggle]')) {
      button.addEventListener('click', () => {
        const input = button.closest('.auth-password-input')?.querySelector('input');
        if (!input) {
          return;
        }

        const isVisible = input.type === 'text';
        input.type = isVisible ? 'password' : 'text';
        button.setAttribute('aria-pressed', String(!isVisible));
        button.setAttribute('aria-label', isVisible ? 'Show password' : 'Hide password');
      });
    }

    const form = app.querySelector('[data-auth-form]');
    if (!form) {
      return;
    }

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      const submitButton = form.querySelector('button[type="submit"]');
      await runBusy(submitButton, () => submitView(form));
    });

    const resendButton = app.querySelector('#resend-confirmation');
    if (resendButton) {
      resendButton.addEventListener('click', async () => {
        await runBusy(resendButton, async () => {
          await authClient.resendSignUpCode(state.email);
          show('confirm-sign-up', {
            notice: 'A new confirmation code was sent to your email.',
            noticeType: 'success'
          });
        });
      });
    }

    const resendOtpButton = app.querySelector('#resend-otp');
    if (resendOtpButton) {
      resendOtpButton.addEventListener('click', async () => {
        await runBusy(resendOtpButton, async () => {
          const result = await authClient.startEmailOtp(state.email);
          state.username = result.username;
          state.session = result.session;
          show('confirm-otp', {
            notice: 'A new sign-in code was sent to your email.',
            noticeType: 'success'
          });
        });
      });
    }
  };

  const submitView = async (form) => {
    clearNotice();

    if (state.view === 'sign-in') {
      state.email = normalizeEmail(form.elements.email.value);
      const password = form.elements.password.value;
      try {
        const result = await authClient.signInWithPassword(state.email, password);
        await completeOrContinue(result);
      } catch (error) {
        if (error && error.name === 'UserNotConfirmedException') {
          await authClient.resendSignUpCode(state.email);
          show('confirm-sign-up', {
            notice: 'Confirm your email to finish creating your account.',
            noticeType: 'info'
          });
          return;
        }
        throw error;
      }
      return;
    }

    if (state.view === 'request-otp') {
      state.email = normalizeEmail(form.elements.email.value);
      const result = await authClient.startEmailOtp(state.email);
      if (result.signedIn) {
        await onSignedIn();
        return;
      }
      state.username = result.username;
      state.session = result.session;
      show('confirm-otp', {
        notice: `We sent a one-time sign-in code to ${maskEmail(state.email)}.`,
        noticeType: 'success'
      });
      return;
    }

    if (state.view === 'confirm-otp') {
      const result = await authClient.confirmEmailOtp(
        state.username || state.email,
        state.session,
        form.elements.code.value
      );
      await completeOrContinue(result);
      return;
    }

    if (state.view === 'sign-up') {
      state.email = normalizeEmail(form.elements.email.value);
      const password = form.elements.password.value;
      const confirmation = form.elements.passwordConfirmation.value;
      if (password !== confirmation) {
        throw new UserFacingError('The passwords do not match.');
      }

      const result = await authClient.signUp(state.email, password);
      if (result.confirmed) {
        await completeOrContinue(await authClient.signInWithPassword(state.email, password));
        return;
      }

      state.pendingPassword = password;
      show('confirm-sign-up', {
        notice: `We sent an account confirmation code to ${maskEmail(state.email)}.`,
        noticeType: 'success'
      });
      return;
    }

      if (state.view === 'confirm-sign-up') {
        await authClient.confirmSignUp(state.email, form.elements.code.value);
        if (state.pendingPassword) {
          const password = state.pendingPassword;
          state.pendingPassword = '';
          try {
            await completeOrContinue(await authClient.signInWithPassword(state.email, password));
          } catch {
            show('sign-in', {
              notice: 'Your email is confirmed. Sign in with your password.',
              noticeType: 'success'
            });
          }
          return;
        }
      show('sign-in', {
        notice: 'Your account is confirmed. Sign in with your password.',
        noticeType: 'success'
      });
      return;
    }

    if (state.view === 'forgot-password') {
      state.email = normalizeEmail(form.elements.email.value);
      await authClient.startPasswordReset(state.email);
      show('reset-password', {
        notice: `We sent a password-reset code to ${maskEmail(state.email)}.`,
        noticeType: 'success'
      });
      return;
    }

    if (state.view === 'reset-password') {
      const password = form.elements.password.value;
      if (password !== form.elements.passwordConfirmation.value) {
        throw new UserFacingError('The passwords do not match.');
      }
      await authClient.confirmPasswordReset(state.email, form.elements.code.value, password);
      show('sign-in', {
        notice: 'Your password has been updated. You can sign in now.',
        noticeType: 'success'
      });
      return;
    }

    if (state.view === 'new-password') {
      const password = form.elements.password.value;
      if (password !== form.elements.passwordConfirmation.value) {
        throw new UserFacingError('The passwords do not match.');
      }
      const result = await authClient.completeNewPassword(state.username, state.session, password);
      await completeOrContinue(result);
    }
  };

  const completeOrContinue = async (result) => {
    if (result && result.signedIn) {
      state.pendingPassword = '';
      await onSignedIn();
      return;
    }

    if (result && result.next === 'NEW_PASSWORD_REQUIRED') {
      state.username = result.username;
      state.session = result.session;
      show('new-password', {
        notice: 'Choose a new password to finish signing in.',
        noticeType: 'info'
      });
      return;
    }

    throw new UserFacingError('Sign-in could not be completed.');
  };

  const clearNotice = () => {
    const notice = app.querySelector('.auth-notice');
    if (notice) {
      notice.remove();
    }
  };

  const runBusy = async (button, task) => {
    if (!button || button.disabled) {
      return;
    }
    const originalText = button.textContent;
    button.disabled = true;
    button.textContent = 'Please wait...';
    try {
      await task();
    } catch (error) {
      state.notice = friendlyAuthError(error);
      state.noticeType = 'error';
      render();
    } finally {
      if (button.isConnected) {
        button.disabled = false;
        button.textContent = originalText;
      }
    }
  };

  render();
}

function renderView(state, { hasMicrosoftSignIn, lastSignInMethod }) {
  if (state.view === 'entry') {
    return `
      <div class="auth-section auth-entry">
        <h2>Welcome</h2>
        <p class="auth-description">Sign in if you have an account, or sign up if you&rsquo;re new.</p>
        ${microsoftSignInOption(hasMicrosoftSignIn)}
        <div class="auth-entry-actions">
          <button class="auth-primary-button" data-auth-view="sign-up" type="button">Sign up</button>
          <button class="auth-secondary-action" data-auth-view="sign-in" type="button">Sign in</button>
        </div>
      </div>
    `;
  }

  if (state.view === 'request-otp') {
    return authForm({
      title: 'Sign in with an email code',
      description: 'We will email you a one-time code. No password is required.',
      fields: emailField(state.email),
      submitLabel: 'Email me a code',
      secondary: backToSignIn()
    });
  }

  if (state.view === 'confirm-otp') {
    return authForm({
      title: 'Enter your sign-in code',
      description: `Use the code sent to ${escapeHtml(maskEmail(state.email))}.`,
      fields: codeField('One-time code'),
      submitLabel: 'Verify and sign in',
      secondary: `
        <button id="resend-otp" class="auth-text-button" type="button">Send a new code</button>
        ${backToSignIn()}
      `
    });
  }

  if (state.view === 'sign-up') {
    return `
      ${microsoftSignInOption(hasMicrosoftSignIn)}
      ${authForm({
        title: 'Create your account',
        description: 'Use your business email address to create a secure account.',
        fields: `${emailField(state.email)}${passwordFields(true)}`,
        submitLabel: 'Create account',
        secondary: accountSwitchPrompt('Already have an account?', 'Sign in', 'sign-in')
      })}
    `;
  }

  if (state.view === 'confirm-sign-up') {
    return authForm({
      title: 'Confirm your email',
      description: `Enter the confirmation code sent to ${escapeHtml(maskEmail(state.email))}.`,
      fields: codeField('Confirmation code'),
      submitLabel: 'Confirm account',
      secondary: `
        <button id="resend-confirmation" class="auth-text-button" type="button">Send a new code</button>
        ${backToSignIn()}
      `
    });
  }

  if (state.view === 'forgot-password') {
    return authForm({
      title: 'Reset your password',
      description: 'Enter your account email and we will send a reset code.',
      fields: emailField(state.email),
      submitLabel: 'Send reset code',
      secondary: backToSignIn()
    });
  }

  if (state.view === 'reset-password') {
    return authForm({
      title: 'Choose a new password',
      description: `Enter the reset code sent to ${escapeHtml(maskEmail(state.email))}.`,
      fields: `${codeField('Reset code')}${passwordFields(true)}`,
      submitLabel: 'Update password',
      secondary: backToSignIn()
    });
  }

  if (state.view === 'new-password') {
    return authForm({
      title: 'Choose a new password',
      description: 'Your account requires a new password before you continue.',
      fields: passwordFields(true),
      submitLabel: 'Save password and sign in',
      secondary: backToSignIn()
    });
  }

  if (state.view === 'sign-in') {
    return `
    ${lastSignInMethod === 'email'
      ? ''
      : microsoftSignInOption(hasMicrosoftSignIn)}

    <div class="auth-section">
      <h2>Sign in with your account</h2>
      <form data-auth-form novalidate>
        ${emailField(state.email)}
        ${passwordFields(false)}
        <div class="auth-password-row">
          <button class="auth-text-button" data-auth-view="forgot-password" type="button">Forgot password?</button>
        </div>
        <button class="auth-primary-button" type="submit">Sign in</button>
      </form>
      <button class="auth-secondary-action" data-auth-view="request-otp" type="button">
        Email me a one-time code instead
      </button>
      ${lastSignInMethod === 'email'
        ? microsoftCompactSignInOption(hasMicrosoftSignIn)
        : ''}
    </div>

    ${accountSwitchPrompt('New to this portal?', 'Create an account', 'sign-up')}
  `;
  }

  return '';
}

function authForm({ title, description, fields, submitLabel, secondary }) {
  return `
    <div class="auth-section auth-subview">
      <h2>${escapeHtml(title)}</h2>
      <p class="auth-description">${escapeHtml(description)}</p>
      <form data-auth-form novalidate>
        ${fields}
        <button class="auth-primary-button" type="submit">${escapeHtml(submitLabel)}</button>
      </form>
      <div class="auth-secondary-links">${secondary}</div>
    </div>
  `;
}

function emailField(email) {
  return `
    <label class="auth-field">
      <span>Email</span>
      <input name="email" type="email" value="${escapeHtml(email)}" autocomplete="username" required>
    </label>
  `;
}

function passwordFields(includeConfirmation) {
  const requirements = includeConfirmation
    ? ' minlength="8" pattern="(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9]).{8,}" aria-describedby="auth-password-requirements"'
    : '';

  return `
    <div class="${includeConfirmation ? 'auth-password-field-group' : ''}">
      ${passwordField({
        id: 'auth-password',
        name: 'password',
        label: 'Password',
        autocomplete: includeConfirmation ? 'new-password' : 'current-password',
        attributes: requirements
      })}
      ${includeConfirmation ? `
        <div class="auth-password-requirements" id="auth-password-requirements" role="note">
          <strong>Password requirements</strong>
          <ul>
            <li>At least 8 characters</li>
            <li>At least 1 uppercase letter</li>
            <li>At least 1 lowercase letter</li>
            <li>At least 1 number</li>
          </ul>
        </div>
      ` : ''}
    </div>
    ${includeConfirmation ? `
      ${passwordField({
        id: 'auth-password-confirmation',
        name: 'passwordConfirmation',
        label: 'Confirm password',
        autocomplete: 'new-password',
        attributes: ' minlength="8"'
      })}
    ` : ''}
  `;
}

function passwordField({ id, name, label, autocomplete, attributes = '' }) {
  return `
    <div class="auth-field">
      <label for="${id}">${escapeHtml(label)}</label>
      <div class="auth-password-input">
        <input id="${id}" name="${name}" type="password" autocomplete="${autocomplete}"${attributes} required>
        <button class="auth-password-toggle" type="button" data-auth-password-toggle aria-label="Show password" aria-pressed="false">
          ${passwordVisibilityIcon()}
        </button>
      </div>
    </div>
  `;
}

function passwordVisibilityIcon() {
  return `
    <svg class="auth-eye-icon auth-eye-show" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M2.1 12s3.6-6 9.9-6 9.9 6 9.9 6-3.6 6-9.9 6-9.9-6-9.9-6Z"/>
      <circle cx="12" cy="12" r="2.75"/>
    </svg>
    <svg class="auth-eye-icon auth-eye-hide" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M3 3l18 18M10.6 6.1A10.4 10.4 0 0 1 12 6c6.3 0 9.9 6 9.9 6a15 15 0 0 1-2.1 2.8M8.4 8.4C6 9.8 2.1 12 2.1 12s3.6 6 9.9 6c1.4 0 2.7-.3 3.8-.8M9.9 9.9a3 3 0 0 0 4.2 4.2"/>
    </svg>
  `;
}

function microsoftSignInOption(hasMicrosoftSignIn) {
  return hasMicrosoftSignIn ? `
    <button id="microsoft-sign-in" class="auth-microsoft-button" type="button">
      ${microsoftIcon()}
      <span>Continue with Microsoft</span>
    </button>

    <div class="auth-divider"><span>or</span></div>
  ` : '';
}

function microsoftCompactSignInOption(hasMicrosoftSignIn) {
  return hasMicrosoftSignIn ? `
    <div class="auth-compact-provider-option">
      <button id="microsoft-sign-in" class="auth-text-button" type="button">Sign in with Microsoft instead</button>
    </div>
  ` : '';
}

function codeField(label) {
  return `
    <label class="auth-field">
      <span>${escapeHtml(label)}</span>
      <input name="code" class="auth-code-input" type="text" inputmode="numeric" autocomplete="one-time-code" required>
    </label>
  `;
}

function backToSignIn(label = 'Back to sign in') {
  return `<button class="auth-text-button" data-auth-view="sign-in" type="button">${escapeHtml(label)}</button>`;
}

function accountSwitchPrompt(prompt, action, view) {
  return `
    <p class="auth-account-switch">
      ${escapeHtml(prompt)}
      <button class="auth-text-button" data-auth-view="${escapeHtml(view)}" type="button">${escapeHtml(action)}</button>
    </p>
  `;
}

function renderNotice(message, type) {
  if (!message) {
    return '';
  }
  return `<div class="auth-notice ${escapeHtml(type || 'info')}" role="alert">${escapeHtml(message)}</div>`;
}

function friendlyAuthError(error) {
  if (error instanceof UserFacingError) {
    return error.message;
  }

  const messages = {
    CodeMismatchException: 'That code is incorrect. Check the code and try again.',
    ExpiredCodeException: 'That code has expired. Request a new code and try again.',
    InvalidPasswordException: 'That password does not meet the security requirements shown below.',
    LimitExceededException: 'Too many attempts were made. Wait a few minutes and try again.',
    TooManyRequestsException: 'Too many attempts were made. Wait a few minutes and try again.',
    NotAuthorizedException: 'The email or password is incorrect.',
    PasswordResetRequiredException: 'This account requires a password reset.',
    UsernameExistsException: 'An account already exists for this email. Sign in or reset your password.',
    UserNotFoundException: 'We could not complete that request. Check the email or create an account.'
  };
  return messages[error && error.name] || 'We could not complete the sign-in request. Please try again.';
}

function microsoftIcon() {
  return `
    <svg class="microsoft-icon" viewBox="0 0 23 23" aria-hidden="true">
      <path fill="#f35325" d="M1 1h10v10H1z"/>
      <path fill="#81bc06" d="M12 1h10v10H12z"/>
      <path fill="#05a6f0" d="M1 12h10v10H1z"/>
      <path fill="#ffba08" d="M12 12h10v10H12z"/>
    </svg>
  `;
}

function maskEmail(email) {
  const [local, domain] = normalizeEmail(email).split('@');
  if (!local || !domain) {
    return email || 'your email';
  }
  const visible = local.slice(0, Math.min(2, local.length));
  return `${visible}${'*'.repeat(Math.max(3, local.length - visible.length))}@${domain}`;
}

function normalizeEmail(value) {
  return String(value || '').trim().toLowerCase();
}

function getInitialAuthView() {
  try {
    if (window.localStorage.getItem(AUTH_ENTRY_SEEN_KEY)) {
      return 'sign-in';
    }
    window.localStorage.setItem(AUTH_ENTRY_SEEN_KEY, '1');
  } catch {
    // If browser storage is unavailable, keep the first-visit choice screen usable.
  }
  return 'entry';
}

function escapeHtml(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

class UserFacingError extends Error {}
